import { useMemo, useState } from 'react';
import { Globe } from 'lucide-react';
import AccordionMenu from '../../../common/accordion-menu';
import Table from '../../../common/table';
import MultiSelect from '../../../common/multi-select';
import { ReactComponent as InfoIcon } from '../../../../assets/icons/info-unfilled.svg';
import { countriesByRegions } from '../../../../config';
import l from '../../../../lang';
import { getCountryFlagUrl } from '../../../../utils';
import { useAvailabilityDays, CountryAvailabilityDays } from '../../../../hooks/use-availability-days';
import Loading from '../../../common/loading';
import './styles.scss';

const formatDays = (days: number | undefined) => {
  if (days === undefined || days === null) return '-';
  return `${days} ${l('paymentMethod.accreditation.days').toLowerCase()}`;
};

interface AccreditationTimesTableProps {
  region: string;
  countriesInRegion: string[];
}

function AccreditationTimesTable({ region, countriesInRegion }: AccreditationTimesTableProps) {
  const [filteredCountries, setFilteredCountries] = useState('');
  const { data, loading, error } = useAvailabilityDays(region);

  const countryOptions = useMemo(
    () =>
      countriesInRegion.map(code => ({
        id: code,
        name: l(`countryCode.${code}`),
      })),
    [countriesInRegion]
  );

  const visibleCountries = useMemo(() => {
    if (!filteredCountries) {
      return countriesInRegion;
    }

    const selected = filteredCountries.split(',').filter(Boolean);
    return countriesInRegion.filter(code => selected.includes(code));
  }, [filteredCountries, countriesInRegion]);

  const countryDataMap = useMemo(() => {
    if (!data) return {};
    const map: Record<string, CountryAvailabilityDays> = {};
    data.forEach(item => {
      map[item.country] = item;
    });
    return map;
  }, [data]);

  if (loading) {
    return (
      <div className="accreditation-times__loading">
        <Loading />
      </div>
    );
  }

  if (error) {
    return <div className="accreditation-times__error">{l('common.error')}</div>;
  }

  return (
    <div className="accreditation-times__table-panel">
      <div className="accreditation-times__filter">
        <MultiSelect
          name="filter-country"
          size="sm"
          label={l('countries.label')}
          placeholderSearch={l('searchCountries.label')}
          titleOptions={l('transactions.filter.country.titleOptions')}
          withSearch
          options={countryOptions}
          onChange={countries => setFilteredCountries(countries || '')}
        />
      </div>

      <div className="accreditation-times__table">
        <Table
          dense
          cellAlign="left"
          withoutEdgeSpacers
          headers={[
            <div className="accreditation-times__country-header">
              <Globe className="accreditation-times__country-header-icon" size={16} strokeWidth={1.5} aria-hidden />
              <span>{l('paymentMethod.country')}</span>
            </div>,
            l('paymentMethods.creditCard'),
            l('paymentMethods.debitCard'),
            l('paymentMethods.bankTransfer'),
            l('paymentMethods.cash'),
          ]}
          body={visibleCountries.map(code => {
            const countryData = countryDataMap[code];
            return [
              <div className="accreditation-times__country-cell">
                <img alt="" className="accreditation-times__country-flag" src={getCountryFlagUrl(code)} />
                <span>{l(`countryCode.${code}`)}</span>
              </div>,
              <span className="accreditation-times__days-cell">{formatDays(countryData?.creditCard)}</span>,
              <span className="accreditation-times__days-cell">{formatDays(countryData?.debitCard)}</span>,
              <span className="accreditation-times__days-cell">{formatDays(countryData?.bankTransfer)}</span>,
              <span className="accreditation-times__days-cell">{formatDays(countryData?.cash)}</span>,
            ];
          })}
        />
      </div>

      <div className="accreditation-times__footer">
        <InfoIcon className="accreditation-times__footer-icon" aria-hidden />
        <span>{l('paymentMethod.accreditation.footerNote')}</span>
      </div>
      <div className="accreditation-times__commission-note">{l('paymentMethod.note')}</div>
    </div>
  );
}

function AccreditationTimes() {
  const items = [
    {
      id: 'LA',
      title: l('region.LA'),
      content: <AccreditationTimesTable region="LA" countriesInRegion={countriesByRegions.LA} />,
    },
    {
      id: 'AF_ME',
      title: l('region.AF_ME'),
      content: <AccreditationTimesTable region="AF_ME" countriesInRegion={countriesByRegions.AF_ME} />,
    },
    {
      id: 'AS',
      title: l('region.AS'),
      content: <AccreditationTimesTable region="AS" countriesInRegion={countriesByRegions.AS} />,
    },
  ];

  return (
    <div className="accreditation-times">
      <div className="payment-methods__subtitle">{l('paymentMethods.accreditationSubtitle')}</div>
      <div className="accreditation-times__content">
        <AccordionMenu items={items} />
      </div>
    </div>
  );
}

export default AccreditationTimes;
